import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "./Cart";
import { Link, useNavigate } from "react-router-dom";
import { cardio } from "ldrs";
import { ChevronDown, Check } from "react-feather";
import ProductCard from "./components/ProductCard";
import {
  isPrescriptionRequired,
  getPrescriptionButtonText,
  getPrescriptionButtonClasses,
} from "./utils/prescriptionUtils";
import {
  filterHiddenCategories,
  filterHiddenCategoriesFromList,
  getProductImage,
  isCategoryHidden,
} from "./utils/categoryUtils";

cardio.register();

const CategoryDropdown = ({ value, categories, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-4 px-6 bg-white border-2 border-neutral-100 rounded-2xl shadow-sm hover:border-primary-300 hover:shadow-md transition-all duration-300 group"
      >
        <span className="text-lg font-bold text-neutral-800 tracking-tight">
          {value}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-neutral-400 transition-transform duration-300 ${isOpen ? "rotate-180 text-primary-500" : "group-hover:text-primary-400"}`} 
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute z-30 mt-3 w-full bg-white border border-neutral-100 rounded-2xl shadow-xl overflow-hidden py-2"
          >
            <div className="max-h-64 overflow-y-auto custom-scrollbar">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    onChange(category);
                    setIsOpen(false);
                  }}
                  className={`flex items-center justify-between w-full px-6 py-3.5 text-left transition-colors duration-200 ${
                    value === category
                      ? "bg-primary-50 text-primary-700 font-bold"
                      : "text-neutral-600 hover:bg-neutral-50 hover:text-primary-600"
                  }`}
                >
                  <span className="text-[15px]">{category}</span>
                  {value === category && (
                    <Check className="w-4 h-4 text-primary-600" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FeaturedProducts = () => {
  const [products, setProducts] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategories, setActiveCategories] = useState(["", "", "", ""]);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const defaultImage =
    "https://images.unsplash.com/photo-1471864190281-a93a3070b6de?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.0.3";

  useEffect(() => {
    const allowedCategories = [
      "supplement",
      "nutritional supplements",
      "dietary supplements",
      "vitamins",
      "deodorant",
      "footcare",
      "walking aid",
      "oral health",
      "cosmetic",
      "oral hygiene",
    ];

    const fetchProductsAndCategories = async () => {
      try {
        console.log("Fetching featured products...");

        // Try featured endpoint first, fallback to regular endpoint with filter
        let productsData = [];
        try {
          const featuredResponse = await fetch(
            "https://dawamaxma.dawamtaani.co.ke/api/pharmacy/featured"
          );
          if (featuredResponse.ok) {
            productsData = await featuredResponse.json();
            console.log("Featured products fetched:", productsData);
          } else {
            throw new Error("Featured endpoint failed");
          }
        } catch (featuredError) {
          console.log("Featured endpoint failed, trying regular endpoint...");
          // Fallback to regular endpoint
          const regularResponse = await fetch(
            "https://dawamaxma.dawamtaani.co.ke/api/pharmacy"
          );
          if (!regularResponse.ok) {
            throw new Error("Regular endpoint also failed");
          }
          const regularData = await regularResponse.json();
          // Handle both old format (array) and new format (object with data)
          const allProducts = regularData.data || regularData;
          // Filter out prescription products on frontend as fallback
          productsData = allProducts.filter(
            (product) =>
              product.Prescription !== "TRUE" &&
              product.Prescription !== true &&
              product.Prescription !== 1
          );
          console.log("Regular products fetched and filtered:", productsData);
        }

        // Filter out hidden categories (Insecticides, Vaccines)
        productsData = filterHiddenCategories(productsData);
        console.log(
          "Products after filtering hidden categories:",
          productsData
        );

        // Group products by category (hidden categories already filtered out)
        const groupedProducts = productsData.reduce((acc, product) => {
          const category = product.category || "Uncategorized";
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(product);
          return acc;
        }, {});

        // Fetch categories
        let categoriesData = [];
        try {
          const categoriesResponse = await fetch(
            "https://dawamaxma.dawamtaani.co.ke/api/pharmacy/categories"
          );
          if (categoriesResponse.ok) {
            categoriesData = await categoriesResponse.json();
            console.log("Categories fetched:", categoriesData);
          } else {
            throw new Error("Categories endpoint failed");
          }
        } catch (categoriesError) {
          console.log(
            "Categories endpoint failed, extracting from products..."
          );
          // Fallback: extract categories from products
          categoriesData = [
            ...new Set(
              productsData.map((product) => product.category).filter(Boolean)
            ),
          ];
          console.log("Categories extracted from products:", categoriesData);
        }

        // Filter categories: only show supplements, deodorant, footcare, walking aid, oral health, cosmetic and oral hygiene
        const filteredCategories = filterHiddenCategoriesFromList(
          categoriesData
        ).filter((category) =>
          allowedCategories.includes(category.toLowerCase())
        );

        // Sort categories to ensure supplements are first if they exist
        filteredCategories.sort((a, b) => {
          if (a.toLowerCase().includes('supplement')) return -1;
          if (b.toLowerCase().includes('supplement')) return 1;
          return 0;
        });

        setCategories(filteredCategories);
        setProducts(groupedProducts);

        if (filteredCategories.length > 0) {
          // Show more categories by default if available
          setActiveCategories(filteredCategories.slice(0, 4));
        }
      } catch (error) {
        console.error("Error fetching products or categories:", error);

        // Fallback to mock data for development/testing
        console.log("Using fallback mock data...");
        const mockProducts = [
          {
            id: 1,
            itemName: "Paracetamol 500mg",
            retailPrice: "150",
            category: "Medicine",
            image: null,
            Prescription: "FALSE",
          },
          {
            id: 2,
            itemName: "Vitamin C Tablets",
            retailPrice: "300",
            category: "Supplements",
            image: null,
            Prescription: "FALSE",
          },
          {
            id: 3,
            itemName: "Hand Sanitizer",
            retailPrice: "200",
            category: "Personal Care",
            image: null,
            Prescription: "FALSE",
          },
        ];

        const mockCategories = ["Supplements", "Deodorant", "Footcare"];

        const filteredMockCategories = mockCategories.filter((category) =>
          allowedCategories.includes(category.toLowerCase())
        );

        // Sort categories to ensure supplements are first if they exist
        filteredMockCategories.sort((a, b) => {
          if (a.toLowerCase().includes('supplement')) return -1;
          if (b.toLowerCase().includes('supplement')) return 1;
          return 0;
        });

        const groupedProducts = mockProducts.reduce((acc, product) => {
          const category = product.category || "Uncategorized";
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(product);
          return acc;
        }, {});

        setCategories(filteredMockCategories);
        setProducts(groupedProducts);
        setActiveCategories(filteredMockCategories.slice(0, 4));

        // Still show error but with fallback data
        setError(
          `API temporarily unavailable. Showing sample data. Error: ${error.message}`
        );
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      }
    };

    fetchProductsAndCategories();
  }, []);

  const handleCategoryChange = (index, newCategory) => {
    setActiveCategories((prev) => {
      const updatedCategories = [...prev];
      updatedCategories[index] = newCategory;
      return updatedCategories;
    });
  };

  // Handle prescription products
  const handleAddToCart = (product) => {
    if (isPrescriptionRequired(product)) {
      setPendingProduct(product);
      setShowPrescriptionModal(true);
    } else {
      addToCart(product);
    }
  };

  const handlePrescriptionUpload = () => {
    if (pendingProduct) {
      addToCart(pendingProduct);
      setShowPrescriptionModal(false);
      setPendingProduct(null);
    }
  };

  if (loading) {
    return (
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <l-cardio
                size="60"
                stroke="4"
                speed="2"
                color="#0ea5e9"
              ></l-cardio>
            </div>
            <motion.p
              className="text-xl text-neutral-600 mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Loading featured products...
            </motion.p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="flex flex-col items-center justify-center py-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-12 h-12 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-neutral-700 mb-4">
              Oops! Something went wrong
            </h3>
            <p className="text-lg text-red-600 text-center max-w-md">{error}</p>
            <motion.button
              onClick={() => window.location.reload()}
              className="mt-6 px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Try Again
            </motion.button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Featured{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">
                Products
              </span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Discover our carefully curated selection of premium healthcare
              products and medications
            </p>
          </motion.div>
        </div>
        {activeCategories.map((activeCategory, index) => {
          const displayedProducts = products[activeCategory]?.slice(0, 5) || [];
          return (
            <div key={index} className="mb-16">
              <div className="mb-8">
                <CategoryDropdown
                  value={activeCategory}
                  categories={categories}
                  onChange={(newCategory) => handleCategoryChange(index, newCategory)}
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 lg:gap-8">
                {displayedProducts.map((product, productIndex) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    defaultImage={defaultImage}
                  />
                ))}
              </div>
            </div>
          );
        })}
        <div className="flex justify-center mt-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Link
              to="/shop"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-secondary-600 to-secondary-700 text-white rounded-xl font-semibold shadow-medium hover:shadow-large hover:from-secondary-700 hover:to-secondary-800 transition-all duration-300 transform hover:-translate-y-1"
            >
              View All Products
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          </motion.div>
        </div>

        {/* Prescription Upload Modal */}
        {showPrescriptionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl p-8 max-w-md w-full"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-2xl font-bold text-neutral-900 mb-4">
                Prescription Required
              </h3>
              <p className="text-neutral-600 mb-6">
                This product requires a prescription. Please upload your
                prescription to add it to your cart.
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Upload Prescription
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPrescriptionModal(false)}
                  className="flex-1 px-4 py-3 bg-neutral-100 text-neutral-700 rounded-xl font-semibold hover:bg-neutral-200 transition-colors duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePrescriptionUpload}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
                >
                  Add to Cart
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProducts;
